import { useCallback, useEffect, useState } from 'react';
import { PlatformApiError, type Member, type ProvisionedMember, type ResetMemberPassword, type Role } from '@sfc/sdk';
import { platform } from '../api/client';

export interface MembersState {
  members: Member[];
  loading: boolean;
  problem?: string;
}

/** The people in an organization, and the things an owner or admin does to them. */
export function useMembers(organizationId: string): MembersState & {
  reload: () => void;
  provision: (input: { email: string; displayName: string; role: Role }) => Promise<ProvisionedMember>;
  changeRole: (membershipId: string, role: Role) => Promise<void>;
  revoke: (membershipId: string) => Promise<void>;
  resetPassword: (membershipId: string) => Promise<ResetMemberPassword>;
} {
  const [state, setState] = useState<MembersState>({ members: [], loading: true });

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const members = await platform.staff.listMembers(organizationId, signal);
        setState({ members, loading: false });
      } catch (error) {
        if (signal?.aborted) return;
        setState({ members: [], loading: false, problem: describe(error) });
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const provision = useCallback(
    async (input: { email: string; displayName: string; role: Role }) => {
      const result = await platform.staff.provisionMember(organizationId, input);
      await load();
      // The whole result, not just the passphrase: whether the address already
      // had an account is the thing the screen has to say out loud when no
      // passphrase comes back.
      return result;
    },
    [organizationId, load],
  );

  const changeRole = useCallback(
    async (membershipId: string, role: Role) => {
      await platform.staff.changeRole(organizationId, membershipId, role);
      await load();
    },
    [organizationId, load],
  );

  const revoke = useCallback(
    async (membershipId: string) => {
      await platform.staff.revokeMember(organizationId, membershipId);
      await load();
    },
    [organizationId, load],
  );

  const resetPassword = useCallback(
    async (membershipId: string) => {
      const result = await platform.staff.resetMemberPassword(organizationId, membershipId);
      await load();
      return result;
    },
    [organizationId, load],
  );

  return { ...state, reload: () => void load(), provision, changeRole, revoke, resetPassword };
}

export function describe(error: unknown): string {
  if (error instanceof PlatformApiError) {
    // The API's own message is written for a person and carries no internals,
    // so it is shown rather than replaced with something vaguer.
    if (error.status === 409 || error.status === 400) return error.message;
    if (error.status === 403) return 'Your role may not do that.';
    if (error.status === 404) return 'That member is no longer there.';
  }
  return 'Something went wrong.';
}
